import { ImageName } from "@mcleod/images";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { Button, ClickEvent, DataSourceMode, EditRowDecorator, Image, Label, Layout, LookupModelSearchEvent, SaveButton, Snackbar, Tab, TableColumn, TableRow, TableRowDisplayEvent, Toast } from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutGeneralRateIncrease } from "./autogen/AutogenLayoutGeneralRateIncrease";
import { TariffManagementTLIAdjustRates } from "./TariffManagementTLIAdjustRates";
import { TariffManagementUtil } from "./TariffManagementUtil";

const SELL_TARIFF_TYPE = "S";
const BUY_TARIFF_TYPE = "S";

export class GeneralRateIncrease extends AutogenLayoutGeneralRateIncrease {

    private adjustRateTLISlideoutLayout = Layout.getLayout("lme/dispatch/TariffManagementTLIAdjustRates") as TariffManagementTLIAdjustRates;
    private adjustRatesTariffLineItemImage: Image;

    override onLoad() {
        this.tabSearchTLIs.expanded = true;
        this.textboxType.setSelectedItemFromValue(SELL_TARIFF_TYPE);
        this.setupTariffLineItemTable();
        TariffManagementUtil.setTableColumnAlignmentToLeft(this.tableTariffLineItem);
        this.styleTab(this.tabSearchTLIs);
        this.mainDataSource.orderBy = [{ field: "gla_uid", sort: "desc" }];
    }

    styleTab(tab: Tab) {
        tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
        tab.headingLabel.setProps({ color: "McLeodPrimary", fontSize: "xlarge", fontBold: true });
    }

    setupTariffLineItemTable() {
        const defaultAdjustTLITooltip = "Adjust Rate selected Tariff Line Items";
        this.tableTariffLineItem.addSelectionListener(() => this.updateImageTooltipAndState(defaultAdjustTLITooltip));
        this.adjustRatesTariffLineItemImage = new Image({
            name: ImageName.adjustRates,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: defaultAdjustTLITooltip,
            enabled: false,
            marginRight: '16',
            onClick: () => this.adjustRateTariffLineItemSlideout()
        });
        this.tableTariffLineItem.addCenterTool(this.adjustRatesTariffLineItemImage);
    }

    adjustRateTariffLineItemSlideout(): void {
        const selectedTableRows: ModelRow[] = this.tableTariffLineItem.selectedRows.map((tableRow: TableRow) => tableRow.data);
        const selectedRowsLength = selectedTableRows.length;
        const layout = this.adjustRateTLISlideoutLayout;
        const title = `Adjust Rates (${selectedRowsLength})`;
        layout.selectedRows = selectedTableRows;
        new EditRowDecorator({
            title: title,
            layout: layout,
            fillVerticalSpace: true,
            width: '60%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.saveButton,
            doAfterSlideIn: () => {
                this.saveButton.enabled = false;
                this.saveButton.busy = false;
                layout.saveButton = this.saveButton;
                layout.textboxAdjustRateByValue.focus();
                layout.textboxEffectiveDate.text = this.textboxEffectiveDate.text;
            },
            onClose: async (cancelled: boolean) => {
                const tableLengthActive = layout.layoutTariffLineItemDataActive.tableTLIData.data?.length > 0;
                const tableLengthExpire = layout.layoutTariffLineItemDataExpire.tableTLIData.data?.length > 0;
                if (tableLengthActive || tableLengthExpire) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = {yesButtonCaption: "Leave",noButtonCaption: "Stay"};
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave === true) {
                        layout.setDefaultLayoutValues();
                        layout.saveButton.enabled = false;
                        layout.slideOut();
                    }
                } else {
                    layout.setDefaultLayoutValues();
                    layout.saveButton.enabled = false;
                    layout.slideOut();
                }
            },
        });
    }

    private saveButton: SaveButton = SaveButton.create({
        id: "applyButton",
        caption: "Save",
        rowBreak: false,
        enabled: false,
        onClick: async (event: ClickEvent) => {
            const layout = this.adjustRateTLISlideoutLayout as TariffManagementTLIAdjustRates;
            layout.saveButton.busy = true;
            const selectedRows = layout.selectedRows as ModelRow[];
            const tableLengthActive = layout.layoutTariffLineItemDataActive.tableTLIData.data?.length > 0;
            const tableLengthExpire = layout.layoutTariffLineItemDataExpire.tableTLIData.data?.length > 0;
            if (tableLengthActive || tableLengthExpire) {
                const message = "Adjusting rates may affect Tariff Line Items. Would you like to proceed with changes?";
                const dialogProps = {yesButtonCaption: "Yes",noButtonCaption: "No"};
                const userSelection = await CommonDialogs.showYesNo(message, "Proceed with Changes?", dialogProps);
                if (userSelection === true) {
                    const tariffInfoObject = layout.buildAdjustRatesObject();
                    if (tariffInfoObject === null) {
                        this.saveButton.busy = false;
                        return;
                    }
                    const effecttiveDate = layout.textboxEffectiveDate.text;
                    if (StringUtil.isEmptyString(effecttiveDate)) {
                        Snackbar.showWarningSnackbar("Provide Effective Date when adjusting Tariff Line Item(s).");
                        this.saveButton.busy = false;
                        return;
                    }
                    const response = await Api.post("lme/dispatch/tariff-line-item-adjust-rates", {
                        "selected_rows": selectedRows,
                        "adjust_rates_info": tariffInfoObject,
                        "effective_date": effecttiveDate
                    });
                    if (response) {
                        layout.saveButton.busy = false;
                        const message = response.data[0].message;
                        const succes = response.data[0].success;
                        if (succes) {
                            Toast.showToast(message);
                            await this.delay(2000);
                            layout.setDefaultLayoutValues();
                            layout.saveButton.enabled = false;
                            layout.slideOut();
                        }
                    }
                } else {
                    layout.saveButton.busy = false;
                    Snackbar.showWarningSnackbar("All Rates Adjustment have been cancelled");
                    layout.setDefaultLayoutValues();
                    layout.saveButton.enabled = false;
                    layout.slideOut();
                }
            } else {
                layout.saveButton.enabled = false;
                layout.saveButton.busy = false;
            }
        }
    })

    updateImageTooltipAndState(defaultAdjustTLITooltip: string) {
        const numberOfRowsSelected = this.tableTariffLineItem.selectedRowCount;
        this.adjustRatesTariffLineItemImage.enabled = numberOfRowsSelected > 0;
        this.adjustRatesTariffLineItemImage.color = numberOfRowsSelected > 0 ? 'primary' : 'default.lightest';
        const adjustTooltip =
            numberOfRowsSelected === 0
                ? defaultAdjustTLITooltip
                : numberOfRowsSelected === 1
                    ? "Adjust Rate 1 selected Tariff Line Item"
                    : `Adjust Rate ${numberOfRowsSelected} selected Tariff Line Items`;
        this.adjustRatesTariffLineItemImage.tooltip = adjustTooltip;
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    buttonClearFormOnClick(event: ClickEvent) {
        this.textboxType.setSelectedItemFromValue(SELL_TARIFF_TYPE);
        this.textboxCustomerId.clear();
        this.textboxTariffID.clear();
        this.textboxEffectiveDate.clear();
        this.layoutTLIAdvancedSearch.sourceTariffLineItem.clear();
        this.layoutTLIAdvancedSearch.sourceTariffLineItem.mode = DataSourceMode.SEARCH;
        this.sourceTariffLineItem.clear();
        this.sourceTariffLineItem.displayDataInBoundComponents();
        this.sourceTariffLineItem.mode = DataSourceMode.NONE;
        this.clearCustomerTable();
    }

    clearCustomerTable() {
        this.sourceCustomer.clear();
        this.sourceCustomer.displayDataInBoundComponents();
        this.tableAffectedCustomers.dataSource.mode = DataSourceMode.NONE
    }

    textboxTariffIDBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        const tariffTypeCode = this.textboxType.selectedItem.value;
        event.filter.tariff_type_code = tariffTypeCode;
    }

    async buttonFilterTLIsOnClick(event: ClickEvent) {
        if (StringUtil.isEmptyString(this.textboxEffectiveDate.text)) {
            Snackbar.showWarningSnackbar("Please provide Effective Date when filtering Tariff Line Item(s).");
            this.textboxEffectiveDate.focus();
            return;
        }
        const filterButton: Button = event.target as Button;
        const filter: ModelRow = this.layoutTLIAdvancedSearch.sourceTariffLineItem.getSearchFilter(false);
        const tableAffectedCustomers = this.tableAffectedCustomers;
        const customerIds: string[] = this.getCustomerIds();
        const tariffIds: string[] = this.getTariffIds();
        if (customerIds && customerIds.length > 0) {
            filter.data.customer_ids = customerIds;
        }
        if (tariffIds && tariffIds.length > 0) {
            filter.data.tariff_ids = tariffIds;
        }
        filter.data.type = this.textboxType.selectedItem?.value;
        filter.data.effective_date = this.textboxEffectiveDate.text;
        const buyTariffColumn: TableColumn = tableAffectedCustomers.getColumnByCaption("Buy Tariff") as TableColumn;
        const sellTariffColumn: TableColumn = tableAffectedCustomers.getColumnByCaption("Sell Tariff") as TableColumn;
        tableAffectedCustomers.setColumnVisibility(buyTariffColumn, filter.data.type === BUY_TARIFF_TYPE);
        tableAffectedCustomers.setColumnVisibility(sellTariffColumn, filter.data.type === SELL_TARIFF_TYPE);
        this.clearCustomerTable();

        if (filter.data) {
            filterButton.busy = true;

            await this.sourceTariffLineItem.search(filter.data).then((result) => {
                const modelRows: ModelRow[] = this.sourceTariffLineItem?.data;
                if (modelRows.length == 0) {
                    TariffManagementUtil.showColumnsForEmptyData(this.tableTariffLineItem, false);
                } else {
                    TariffManagementUtil.dynamicallyShowColumn(this.tableTariffLineItem, modelRows);
                    const lastRowIndex: number = result.modelRows.length-1;
                    const affectedCustomers: ModelRow[] = result.modelRows[lastRowIndex].data?.affected_customers as ModelRow[];
                    if (affectedCustomers.length) {
                        const ids: string[] = affectedCustomers.map(customer => customer["id"]);
                        tableAffectedCustomers.dataSource.search({ customer_ids: ids });
                    }
                }
                this.tabSearchTLIs.expanded = false;
            }).finally(() => {
                filterButton.busy = false;
            });

            this.tabSearchTLIs.expanded = false;
        }
    }

    getCustomerIds(): string[] {
        const customerIds: string[] = [];
        const lookupRows: ModelRow[] = this.textboxCustomerId.lookupModelData;
        if (lookupRows && lookupRows.length > 0) {
            lookupRows.forEach((customerRow: ModelRow) => {customerIds.push(customerRow.data["id"])});
        }
        return customerIds;
    }

    getTariffIds(): string[] {
        const tariffIds: string[] = [];
        const lookupRows: ModelRow[] = this.textboxTariffID.lookupModelData;
        if (lookupRows && lookupRows.length > 0) {
            lookupRows.forEach((tariffRow: ModelRow) => {tariffIds.push(String(tariffRow.data["tariff_id"]))});
        }
        return tariffIds;
    }

    tableTariffLineItemOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow()
        const labelTariffId = tableRow.findComponentById("labelTariffId") as Label;
        TariffManagementUtil.tableRowDisplayLogic(event);
        const tariffID = tableRow.data.get("tariff_id");
        if (!StringUtil.isEmptyString(tariffID) && labelTariffId !== undefined) {
            CommonTooltips.setTooltipContentLayout(labelTariffId, null, "lme/dispatch/TariffQuickInfo", null, { tariff_id: tariffID });
        }
    }
}
